/**
 * Launcher V2 — AI copy generation, browser-safe rules (U3a of
 * plans/2026-09-10-001-launcher-v2-ai-copy-editing-and-usage-sidebar-plan.md,
 * §U3, coding-standards.md §1).
 *
 * ISOMORPHIC — no server imports (`_shared/anthropic`, `_shared/db-client`,
 * `node:*`). `copy-generation.ts` (server-only concerns: the actual model
 * call) imports from here; so does the U7b configure form in the browser
 * (`isThinDescription`, `DEFAULT_GENERATION_INSTRUCTION`, `readableEventName`).
 * A server import here would break the Vite bundle.
 */
import { COPY_VARIABLE_FIELDS } from '../_shared/launcher-copy-tokens';
import type {
  PlaybooksGenerationInputs,
  PlaybooksGenerationPreset,
  PlaybooksGenerationStrategy,
  PlaybooksRosterStatusCount,
} from './schemas';

// ─── Generation defaults (browser-safe) ──────────────────────────────────────

/**
 * Model selection is a plain code constant — NO settings table (see
 * `copy-generation.ts`'s file header). Lives HERE, not in `copy-generation.ts`
 * (which imports `_shared/anthropic` and is server-only), because
 * `GenerateWithAIDrawer.tsx#currentGenerationInputs` needs this same value
 * client-side to fill `generationInputs.model` on Apply — `copy-generation.ts`
 * re-exports it so every existing server-side import keeps working unchanged.
 */
export const DEFAULT_MODEL = 'claude-sonnet-5-5';

/**
 * Ship default (KTD 7, Spec B §6/§15) — `'whole'` until the harness
 * measurement shows `'per-step'` winning. Moved here for the same
 * browser-bundle-safety reason as {@link DEFAULT_MODEL}:
 * `GenerateWithAIDrawer.tsx#currentGenerationInputs` needs a fallback
 * strategy value for a `GeneratedCopyReview` entry that predates the
 * `strategy` field.
 */
export const DEFAULT_GENERATION_STRATEGY: PlaybooksGenerationStrategy = 'whole';

// ─── Merge-token vocabulary ──────────────────────────────────────────────────

/**
 * Every merge token that resolves at send time, spelled exactly. Reused from
 * the v1 vocabulary (`COPY_VARIABLE_FIELDS`) rather than restated — a second
 * list would drift, and a drifted token renders literally to the recipient
 * instead of resolving. Moved here (from `copy-generation.ts`) because
 * `allowedTokensForSequence` — a browser-safe function — needs it too.
 */
export const ALLOWED_COPY_TOKENS: string[] = COPY_VARIABLE_FIELDS.map((f) => f.variable);

/**
 * The three tokens that name the CAMPAIGN OBJECT ITSELF (`{{Event Name}}`,
 * `{{Event Date}}`, `{{Campaign Type}}`) — decision 4 (Spec B §6): campaign
 * facts must be emitted as plain text ("name the event as <eventName>"), not
 * as a merge token, because these three never resolved reliably at send time
 * for a rewritten sequence. A SUBTRACTION SET over `ALLOWED_COPY_TOKENS`
 * (filtered by the real `object: 'Campaign'` field), never a re-listing — a
 * hand-typed second list would drift from the source vocabulary.
 */
export const CAMPAIGN_OBJECT_TOKENS: string[] = COPY_VARIABLE_FIELDS.filter(
  (f) => f.object === 'Campaign',
).map((f) => f.variable);

/**
 * Matches every `{{...}}` merge token, in encounter order.
 *
 * `[^}]*` matches `_shared/launcher-copy-tokens.ts`'s `TOKEN_PATTERN_SOURCE`
 * exactly (U1, Codex A6): empty braces ARE a token as far as both the resolver
 * and the authoring scanner are concerned, so the generation allowlist has to
 * see `{{}}` in model output and refuse it rather than pass it through to a
 * send that will then skip the member.
 */
export const TOKEN_PATTERN = /\{\{[^}]*\}\}/g;

/** Every `{{...}}` token literally present in `text`, spelled exactly as written, in order. */
export function tokensIn(text: string): string[] {
  return [...text.matchAll(TOKEN_PATTERN)].map((m) => m[0]);
}

/** The subset of a `SequenceStep` that token rules read — subject/body only. */
export interface StepTokenSource {
  subject?: string;
  body?: string;
}

/**
 * The token rule (decision 10, D10; U9/Key Technical Decision 11 inverts the
 * old "plus literal tokens already present" half): only tokens in
 * `ALLOWED_COPY_TOKENS` minus the three campaign-object tokens — full stop.
 * A non-Terra token literally present in the original step (Gong's own
 * `{{recipient.*}}`/`{{sender.*}}`/`{{contact.*}}` syntax, a raw SFDC
 * placeholder like `{{contact.Owner_Meeting_Link__c}}`, or a Handlebars
 * conditional) is never added back into the allowed set — it must not be
 * emitted by the model at all, because a non-Terra token can no longer be
 * sent: `sequence-token-flags.ts`'s pure derivation flags it for the rep
 * separately, and the AI pass is not the place that "helps" by keeping it
 * alive.
 *
 * D10's other half still holds — ONE set for the WHOLE sequence, not a
 * per-step re-derivation: the prompt's `## Available tokens` section
 * (`copy-generation.ts`'s `availableTokensSection`) and
 * `validateAndAssemble`'s per-step validation loop both call this SAME
 * function, so what the prompt advertises and what the validator accepts can
 * never drift apart. `steps` is accepted (and unused) only so every existing
 * call site (`allowedTokensForSequence(baseSteps)`) keeps compiling
 * unchanged — the per-sequence signature is D10's contract even though the
 * result is no longer sequence-shaped.
 */
export function allowedTokensForSequence(_steps: readonly StepTokenSource[]): string[] {
  return ALLOWED_COPY_TOKENS.filter((token) => !CAMPAIGN_OBJECT_TOKENS.includes(token));
}

// ─── Gong merge-token normalization (U8/U2, plan Key Technical Decisions 9-10) ─
//
// Imported Gong steps carry Gong's OWN merge-token syntax
// (`{{recipient.first_name}}`, `{{sender.first_name}}`,
// `{{contact.ClayDR_Meeting_Link__c}}`, …) rather than Terra's
// `{{Token}}` vocabulary (`ALLOWED_COPY_TOKENS` above).
//
// WHERE normalization runs, and why it exists at all: see
// {@link normalizeGongTokensInSteps}' doc below — the canonical account.
//
// It is still NEVER called inside the AI generation/validation pass in
// `copy-generation.ts`. U9 (Key Technical Decision 11) inverted that
// pass's old guard: it used to let a non-standard token like
// `{{recipient.first_name}}` survive literally in the model's output because
// `allowedTokensForSequence` allowed whatever was already literally present.
// Now Gong tokens must not survive to SEND — `copy-generation.ts`'s allowlist
// check (~line 632) refuses any output token outside `ALLOWED_COPY_TOKENS`
// minus `CAMPAIGN_OBJECT_TOKENS`, full stop, so a raw Gong or SFDC placeholder
// reaching the model's output is a refusal, not a pass-through. The rep still
// finds out about it — `sequence-token-flags.ts` (Key Technical Decision 10)
// flags an unmapped Gong token on the ORIGINAL step for the rep to see before
// generation ever runs; the flag, not the AI, tells the rep. This map and
// `normalizeGongMergeTokens` still must never be called from the AI pass —
// that pass's job is now simpler (refuse non-Terra tokens), not "normalize
// them instead."
//
// Anything Gong sends that these normalization points leave unmapped is never
// silently kept: it is a rep-visible flag, not a second, looser normalization
// pass. `{@link GONG_TOKENS_WITHOUT_TERRA_EQUIVALENT}`
// names every such token explicitly (Key Technical Decision 9 — provably
// seeded from mined production data, `__tests__/fixtures/gong-observed-token-vocab.json`,
// partition-tested with no remainder), and `sequence-token-flags.ts` (Key
// Technical Decision 10) is the one pure derivation over `subject`/`body`
// text that reports it — to the step badge, the rail card, the library
// editor, and the server resolution report — never a stored column.

/**
 * Gong wire token key (the text between `{{` and `}}`, Gong's own dotted
 * `object.field` spelling) → the equivalent Terra token NAME (no braces —
 * {@link normalizeGongMergeTokens} adds them). Extend this table as more
 * Gong tokens turn up in real production flow data; every value here must
 * also be a real entry in {@link ALLOWED_COPY_TOKENS} (enforced by the
 * INVARIANT test in `__tests__/copy-generation-rules.test.ts`). Seeded
 * (U2) from `__tests__/fixtures/gong-observed-token-vocab.json` — 58
 * distinct tokens mined across 723 real production Gong flows, 2026-09-15.
 */
export const GONG_MERGE_TOKEN_MAP: Readonly<Record<string, string>> = {
  'recipient.first_name': 'First Name',
  'recipient.last_name': 'Last Name',
  'recipient.job_title': 'Title',
  'recipient.email': 'Email',
  'contact.FirstName': 'First Name',
  'sender.first_name': 'Sender First Name',
  'sender.primary_scheduling_link': 'Sender Meeting Link',
  'contact.ClayDR_Meeting_Link__c': 'ClayDR Meeting Link',
  'contact.ClayDR_First_Name__c': 'ClayDR First Name',
  'contact.ClayDR_Email__c': 'ClayDR Email',
  'contact.Growth_Strategist_Meeting_Link__c': 'GS Meeting Link',
  'contact.Growth_Strategist_First_Name__c': 'GS Owner First Name',
  'contact.Growth_Strategist_Full_Name__c': 'GS Owner Name',
  'contact.GTME_First_Name__c': 'GTME Owner First Name',
};

/**
 * Every other (non-conditional) Gong wire token observed in real production
 * data (`__tests__/fixtures/gong-observed-token-vocab.json`, 58 tokens / 723
 * flows, mined 2026-09-15) that has NO Terra equivalent — listed explicitly,
 * by design (Key Technical Decision 9), so the fixture-partition test in
 * `__tests__/copy-generation-rules.test.ts` has no remainder: every observed
 * token is either a {@link GONG_MERGE_TOKEN_MAP} key, a member of this set,
 * or a conditional marker ({@link HANDLEBARS_CONDITIONAL_PATTERN}). Adding a
 * token to the fixture without deciding its fate here fails that test.
 *
 * FLAG-AND-LEAVE, never a normalization target — `sequence-token-flags.ts`
 * reports membership here as `gong_unmapped` with `terraEquivalent: null`;
 * nothing in this table is ever rewritten. Salesforce custom fields (`__c`)
 * are PERMANENTLY here: Terra has no equivalent contact/account custom-field
 * vocabulary and never will (workspace-specific SFDC schema, not a Terra
 * merge token).
 */
export const GONG_TOKENS_WITHOUT_TERRA_EQUIVALENT: ReadonlySet<string> = new Set([
  // Blocked on purpose. Clay's standardizer owns the company name, and
  // `{{Company Name}}` IS a rep-facing Terra token again since 2026-09-16 —
  // but as a Clay-owned DEFERRAL (Terra emits it literally; see
  // `_shared/launcher-copy-tokens.ts`'s `CLAY_OWNED_TOKENS`), not as something
  // Terra resolves. Whether this Gong spelling should become an ALIAS of it in
  // `GONG_MERGE_TOKEN_MAP` (and so auto-normalize on import) is a deliberate
  // follow-up decision, not an oversight: it interacts with the
  // Gong-normalization seam and wants its own product call.
  'recipient.company_name',

  'sender.last_name',

  // contact.Owner_*__c — the SFDC record OWNER's fields; Terra's owner
  // vocabulary is GTME/GS/ClayDR-specific (see GONG_MERGE_TOKEN_MAP above),
  // not a generic "Owner" concept, so these have no Terra equivalent.
  'contact.Owner_Meeting_Link__c',
  'contact.Owner_Email__c',
  'contact.Owner_First_Name__c',
  'contact.Owner_Full_Name__c',

  // Every remaining contact.* Salesforce field observed — no Terra token
  // exists for any of these; permanent, not a to-do list.
  'contact.GTM_Play_Copy_1__c',
  'contact.Inbound_Use_Cases__c',
  'contact.Qualification_Status__c',
  'contact.First_Clay_User_Created__c',
  'contact.Last_Inbound_Form_Submission_Date__c',
  'contact.Product_Tier__c',
  'contact.Inbound_Source__c',
  'contact.MasterRecordId',
  'contact.MobilePhone',
  'contact.MailingCountry',
  'contact.GTM_Play_Copy_2__c',
  'contact.Solutions_Partner_Name__c',
  'contact.Best_Phone__c',
  'contact.Conversion_Copy_1__c',
  'contact.Inbound_Email_Volume__c',
  'contact.Inbound_Tech_Stack__c',
  'contact.Inbound_Biggest_Problem__c',
  'contact.Inbound_Agency__c',
  'contact.Conversion_Copy_2__c',
  'contact.Conversion_Copy_3__c',
  'contact.Conversion_Copy_4__c',
  'contact.Inbound_Call_Copy__c',
  'contact.Inbound_Implementation_Partner__c',
  'contact.Cancelled_Call_Copy_1__c',
  'contact.Pre_Meeting_Email_Copy__c',
  'contact.GTM_Play_Copy_3__c',
  'contact.Active_Partner_Name__c',

  // Every remaining account.* Salesforce field observed.
  'account.Type',
  'account.Number_of_Open_Opp__c',
  'account.Product_Tier__c',
  'account.Website',
  'account.Named_Account_Status__c',
  'account.NumberOfEmployees',
  'account.Owner_Meeting_Link__c',
]);

/**
 * Matches one Handlebars BLOCK-HELPER marker — `{{#if …}}`, `{{#unless …}}`,
 * any other `{{#helper …}}` opener, its matching `{{/helper}}` closer, or a
 * bare `{{else}}` — as a single occurrence, never as a merge token (Key
 * Technical Decision 9). Gong flows use these to gate a greeting on whether
 * a first name is present; Terra's copy vocabulary has no conditional
 * concept, so every marker this matches is always a rep-visible flag
 * (`sequence-token-flags.ts`'s `'conditional'` kind), never something to
 * normalize or render.
 *
 * ── THE SHAPE IS ABOUT BACKTRACKING, NOT ABOUT MATCHING (U7, Codex A8) ──────
 * Was `/\{\{\s*(?:#\w+[^}]*|\/\w+|else)\s*\}\}/g`, which matched exactly the
 * same markers and took 4,384 ms on a 6,403-character input (`'{{#' + 'a'x3200
 * + ' 'x3200`, measured). `#\w+` and `[^}]*` can match the same characters and
 * sit directly against each other, and the trailing `\s*` overlapped both, so
 * an opener with no closing brace had roughly cubically many ways to split the
 * input between the three quantifiers — all of which the engine tries before
 * concluding there is no match.
 *
 * Two changes remove the overlap and nothing else: `#\w` is a SINGLE character
 * (fixed length, so `[^}]*` has nothing to share with it), and the trailing
 * `\s*` is scoped inside the `/helper` / `else` branches, which are bounded.
 * Same input, 0 ms; 40,000 characters, 0 ms.
 *
 * This is not a theoretical hazard: `sequenceTokenFlags` runs this
 * synchronously in the copy editor on every keystroke and again inside the
 * server's resolution report, and `SequenceStepSchema` deliberately puts no
 * length bound on a body (Gong's HTML bodies are legitimately large, which is
 * what the 10 MB parser exists for). The regression guard is a TIMING bound in
 * `__tests__/copy-generation-rules.test.ts` — an equivalence test passes
 * against both patterns and so cannot catch this class at all.
 */
export const HANDLEBARS_CONDITIONAL_PATTERN = /\{\{\s*(?:#\w[^}]*|(?:\/\w+|else)\s*)\}\}/g;

/**
 * Every Handlebars conditional marker literally present in `text`, in
 * encounter order — the same "spelled exactly, in order" contract as
 * {@link tokensIn}, scoped to conditional markers only.
 */
export function conditionalMarkersIn(text: string): string[] {
  return [...text.matchAll(HANDLEBARS_CONDITIONAL_PATTERN)].map((m) => m[0]);
}

/** Matches `{{ key }}`, capturing `key` — tolerant of inner whitespace, unlike {@link TOKEN_PATTERN}. */
const GONG_TOKEN_PATTERN = /\{\{\s*([A-Za-z0-9_.]+)\s*\}\}/g;

/**
 * Rewrites every `{{...}}` occurrence in `text` whose inner key is a
 * {@link GONG_MERGE_TOKEN_MAP} key into Terra's `{{<Terra name>}}` spelling.
 * Everything else is left byte-for-byte unchanged: an already-Terra token
 * (`{{First Name}}`, whose inner text is not a map key), and an unknown Gong
 * token (`{{contact.Owner_Meeting_Link__c}}`, not yet in the table) both pass
 * through untouched — the latter is exactly the case the AI-pass remap guard
 * exists to protect once this function has already run at the fetch boundary.
 */
export function normalizeGongMergeTokens(text: string): string {
  return text.replace(GONG_TOKEN_PATTERN, (whole, key: string) => {
    const terraName = GONG_MERGE_TOKEN_MAP[key];
    return terraName === undefined ? whole : `{{${terraName}}}`;
  });
}

/**
 * The copy fields {@link normalizeGongTokensInSteps} rewrites. Structural
 * rather than `SequenceStep` so this module stays free of `schemas.ts` (and
 * its zod), and generic at the call site so a real `SequenceStep` array comes
 * back OUT as a `SequenceStep` array — `copyStatus`, `gongStepDescriptor` and
 * anything added later included.
 */
interface NormalizableStep {
  subject?: string;
  body?: string;
}

/**
 * {@link normalizeGongMergeTokens} lifted to ONE step snapshot — its subject
 * and body, everything else untouched. THE aliasing primitive: every call
 * site (the server read seam, the save route, and all four client resolvers
 * in `copy-token-flags.ts`) goes through this or the array form below, which
 * is a `map` over it. See that function's doc for why normalization exists
 * and where it runs.
 *
 * Returns the SAME object when nothing changes.
 */
export function normalizeGongTokensInStep<T extends NormalizableStep>(step: T): T {
  const subject = step.subject === undefined ? undefined : normalizeGongMergeTokens(step.subject);
  const body = step.body === undefined ? undefined : normalizeGongMergeTokens(step.body);
  if (subject === step.subject && body === step.body) return step;
  // Spread FIRST, then overwrite only the two copy fields — and only the
  // ones that exist: writing `subject: undefined` onto a step that never had
  // a subject would add the key (a LinkedIn-style step legitimately has
  // none), changing the snapshot's shape on a mere read.
  const next = { ...step };
  if (subject !== undefined) next.subject = subject;
  if (body !== undefined) next.body = body;
  return next;
}

/**
 * CANONICAL "why" for Gong merge-token normalization. Everywhere else that
 * calls it carries a one-line pointer here, not a retelling.
 *
 * {@link normalizeGongTokensInStep} lifted to a whole step snapshot.
 *
 * ── WHY IT EXISTS (prod QA, 2026-09-16, Fix 4) ───────────────────────────────
 * It used to run at the Gong FETCH boundary only
 * (`gong-flow-picker-api.ts#gongStepsToSequenceSteps`), so only sequences
 * imported after that boundary shipped were ever converted. Anything imported
 * BEFORE it still stores Gong's spellings in
 * `playbooks_sequence_version.steps` — verified in production on "Dreamforce
 * Follow Up" (2026-09-15), whose head still holds `{{recipient.first_name}}`.
 * Every other path classified those as `unknown`, so the sequence previewed as
 * raw Gong text and its send marked EVERY member a copy defect: it sent to
 * nobody. A mappable Gong token is an ALIAS of its Terra token, so it is
 * resolved as one everywhere instead.
 *
 * ── WHERE IT RUNS — three layers, each for its own reason ────────────────────
 *  1. `versioning.ts#loadSequenceVersion`, the one SERVER read seam every
 *     sequence-version read passes through: this is what makes legacy stored
 *     content work with no data migration and no historical row rewritten.
 *  2. `api.ts#saveSequenceSteps`, on write, so stored copy converges on Terra
 *     spellings from the next save onward.
 *  3. `copy-token-flags.ts`' four client resolvers, for the one window the
 *     server seam cannot reach: copy the rep pasted but has not saved yet.
 * NEVER inside the AI pass. `copy-generation.ts` re-parses the MODEL's output
 * with the same `SequenceStepsSchema`, and that pass must keep REFUSING a
 * non-Terra token rather than quietly rewriting it (Key Technical Decision 11)
 * — which is also why this is not a schema transform, tempting as that is.
 *
 * Returns the SAME array (and the same step objects) when nothing changes —
 * the overwhelmingly common case. Idempotent: a Terra spelling is not a
 * {@link GONG_MERGE_TOKEN_MAP} key, so all three layers can apply it in turn.
 */
export function normalizeGongTokensInSteps<T extends NormalizableStep>(steps: T[]): T[];
export function normalizeGongTokensInSteps<T extends NormalizableStep>(
  steps: readonly T[],
): readonly T[];
export function normalizeGongTokensInSteps<T extends NormalizableStep>(
  steps: readonly T[],
): readonly T[] {
  let anyRewritten = false;
  const normalized = steps.map((step) => {
    const next = normalizeGongTokensInStep(step);
    if (next !== step) anyRewritten = true;
    return next;
  });
  return anyRewritten ? normalized : steps;
}

// ─── Thin-context detection ───────────────────────────────────────────────────

/** Below this many characters, a campaign description is "thin" regardless of word count. */
export const THIN_DESCRIPTION_CHARS = 200;
/** Below this many words, a campaign description is "thin" regardless of character count. */
export const THIN_DESCRIPTION_WORDS = 30;

/**
 * True when `text` is too short to carry real campaign facts — the UI's
 * "add more context" warning and `generateSequenceCopy`'s `thinContext` flag
 * both key off this. Either measure alone can trigger it (a short but
 * dense description and a long but sparse one are both thin in different ways).
 */
export function isThinDescription(text: string): boolean {
  const trimmed = text.trim();
  const wordCount = trimmed === '' ? 0 : trimmed.split(/\s+/).length;
  return trimmed.length < THIN_DESCRIPTION_CHARS || wordCount < THIN_DESCRIPTION_WORDS;
}

// ─── Readable event name ──────────────────────────────────────────────────────

const TIER_TAG = /^T\d+$/i;
const QUARTER_DATE_TAG = /^Q[1-4]['’]?\s*\d{2,4}$/i;
const ISO_DATE_TAG = /^\d{4}-\d{2}-\d{2}$/;
/** All-caps 2-6 letter tags (`EMEA`, `APAC`, `NA`, `ANZ`, ...) — region tags. */
const REGION_TAG = /^[A-Z]{2,6}$/;

function isDroppableTag(tag: string): boolean {
  return (
    TIER_TAG.test(tag) || QUARTER_DATE_TAG.test(tag) || ISO_DATE_TAG.test(tag) || REGION_TAG.test(tag)
  );
}

/**
 * Recursively drops the LAST " — "/" - "/" | " separated segment when it
 * matches a tier, quarter/date, or region tag, stopping at the first segment
 * from the end that is not one of those (so a real title containing a dash
 * — "Ads in Clay - Workshop" — is left alone).
 */
function stripTrailingTags(text: string): string {
  const match = /^(.*?)\s*[—\-|]\s*([^—\-|]+)$/.exec(text);
  if (!match) return text;
  const [, rest, tag] = match;
  if (isDroppableTag(tag.trim())) {
    return stripTrailingTags(rest.trimEnd());
  }
  return text;
}

/**
 * Strips a campaign's internal bookkeeping tags — leading `[...]`/`(...)`
 * groups and trailing tier/quarter-date/region tags — down to the name a
 * rep-facing prompt or UI label should show. Tuned against real Salesforce
 * campaign names in `mini-apps/playbooks/docs/playbooks-copy-gen-test-campaigns.md`.
 * Falls back to the raw (trimmed) name when stripping would leave nothing —
 * a name that IS entirely a bracket tag (`"[Internal]"`) still needs a label.
 */
export function readableEventName(name: string): string {
  const withoutGroups = name.replace(/\[[^\]]*\]/g, '').replace(/\([^)]*\)/g, '');
  const collapsed = withoutGroups.replace(/\s+/g, ' ').trim();
  const stripped = stripTrailingTags(collapsed).trim();
  return stripped === '' ? name.trim() : stripped;
}

// ─── Generation-preset derivation ─────────────────────────────────────────────

/**
 * Derives a sequence's `generation_preset` (the identity-level "last
 * generation inputs" the "Generate with AI" drawer preselects, U9) from a
 * completed run's full {@link PlaybooksGenerationInputs}. The ONLY preset
 * derivation (coding-standards.md §6) — the confirm endpoint's former inline
 * copy at `api.ts` (pre-U7a) called this same logic in place; U7a's
 * save-steps handler is the second call site, not a second implementation.
 *
 * Drops `generatedAt`/`model`/`strategy`/`sourceHeadVersionId` — the
 * run-specific fields `PlaybooksGenerationPresetSchema.lastInputs` does not
 * accept (`schemas.ts`'s `.omit({...})` on the same four keys) — by
 * hand-picking the surviving fields rather than an object-spread-then-delete,
 * so a new field added to `PlaybooksGenerationInputsSchema` without an
 * explicit decision here fails the round-trip test instead of silently
 * leaking into the preset.
 */
export function presetFromGenerationInputs(inputs: PlaybooksGenerationInputs): PlaybooksGenerationPreset {
  return {
    campaignSfdcId: inputs.campaignSfdcId,
    lastInputs: {
      campaignSfdcId: inputs.campaignSfdcId,
      eventName: inputs.eventName,
      campaignDescription: inputs.campaignDescription,
      memberStatuses: inputs.memberStatuses,
      instruction: inputs.instruction,
      sourceMaterial: inputs.sourceMaterial,
    },
  };
}

// ─── Audience framing (G2, round-4 Decision 8 / Decision 15) ─────────────────

/**
 * The `Framing:` + `Targeted status counts:` lines `audienceSection`
 * (`copy-generation.ts`) appends after its two existing lines. Fixes the G2
 * bug: Attended + No-show both selected produced "thanks for attending"
 * copy, because nothing told the model that a mixed status selection can
 * imply CONTRADICTORY participation outcomes.
 *
 * Deliberately a RULE the model applies, never a status→semantics table
 * (Decision 8, rejected per the no-hardcoded-config-fallback convention —
 * `cm_status` ladders are per-campaign Salesforce configuration, not a fixed
 * vocabulary this code could enumerate). The only status-specific text this
 * function ever emits is the status name(s) the caller supplied, echoed back
 * verbatim — it never branches on what a given status name MEANS.
 *
 * - No filter (`memberStatuses` empty) — every member is targeted regardless
 *   of status, so statuses necessarily differ; neutral framing, no counts
 *   line (there is nothing to count against — the roster mix line already
 *   covers the whole campaign).
 * - One status — every targeted member shares it; the model may address that
 *   outcome directly, exactly as far as the status name itself supports
 *   (this function does not interpret what "No-show" or "Attended" implies).
 * - Several statuses — they may imply different participation outcomes;
 *   assert only what is true of every one of them, falling back to the same
 *   neutral phrase as the no-filter case.
 *
 * `<event name>` is a LITERAL placeholder, not `${eventName}` interpolation —
 * the model substitutes the real event name it was given elsewhere in the
 * prompt; keeping it literal here keeps this function pure and deterministic
 * for tests, independent of `CopyGenerationInputs.eventName`.
 */
export function audienceFraming(
  memberStatuses: readonly string[],
  rosterStatusCounts: readonly PlaybooksRosterStatusCount[],
): string[] {
  const NEUTRAL_PHRASE = 'thanks for your interest in <event name>';

  if (memberStatuses.length === 0) {
    return [
      `Framing: every member of the campaign is targeted regardless of status; statuses differ, so use neutral framing — "${NEUTRAL_PHRASE}" — and do not assert attendance or absence.`,
    ];
  }

  const framingLine =
    memberStatuses.length === 1
      ? `Framing: every targeted member has the status "${memberStatuses[0]}" — you may address that outcome directly, but only as far as the status name itself supports it.`
      : `Framing: the targeted statuses differ (${memberStatuses.join(', ')}). Assert only what is true of every one of them; if they imply different participation outcomes, use neutral framing — "${NEUTRAL_PHRASE}" — and do not claim attendance or absence.`;

  const countsLine =
    rosterStatusCounts.length === 0
      ? 'Targeted status counts: (none on roster)'
      : `Targeted status counts: ${memberStatuses
          .map((status) => `${status} — ${rosterStatusCounts.find((r) => r.status === status)?.count ?? 0}`)
          .join('; ')}`;

  return [framingLine, countsLine];
}

// ─── Default generation instruction ──────────────────────────────────────────

/**
 * The rep's default brief when they haven't typed their own — rendered under
 * the prompt's `## Instruction` section (Spec B §6). Deliberately generic:
 * a rep who wants something more specific edits it before generating.
 */
export const DEFAULT_GENERATION_INSTRUCTION =
  'Rewrite this sequence for the chosen audience using the campaign facts and description. Keep the existing cadence, match the register and length of the current copy, and introduce no invented facts.';
